import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Header from '../Header';
import GameTable from '../GameTable';
import { useAuth } from '../../context/AuthContext';
import { GameWsClient } from '../../services/online/wsClient';
import OnlineTableRenderer from '../../game/online/OnlineTableRenderer';
import { toVisualSlot } from '../../game/online/onlineSeat';
import { getAiPersona } from '../../game/online/aiPersonas';
import ToastManager from '../../game/ToastManager';
import RondaLogic from '../../game/RondaLogic';
import ScoreMarker from '../../game/ScoreMarker';
import soundVibrationService from '../../services/SoundVibrationService';
import useTeamColors from '../../hooks/useTeamColors';
import useIsMobileDevice from '../../hooks/useIsMobileDevice';
import useLandscapeOrientation from '../../hooks/useLandscapeOrientation';
import { TOAST_ICONS, SEPARATOR, formatTeam, formatPoints } from '../../config/toastIcons';
import RondaDecisionButtons from '../RondaDecisionButtons';
import CaidaOpportunityButtons from '../CaidaOpportunityButtons';
import RoundTransitionModal from './RoundTransitionModal';
import MatchWinnerModal from './MatchWinnerModal';
import ShufflingModal from '../ShufflingModal';
import RondaHandSummaryModal from '../RondaHandSummaryModal';
import { GAME_TIMINGS } from '../../config/gameTimings';
import useRoomChat from '../../hooks/useRoomChat';
import useChatUnread from '../../hooks/useChatUnread';
import ChatPanel from './ChatPanel';
import { EMOTE_PRESETS } from './chatPresets';
import OnlineSettingsModal from './OnlineSettingsModal';

// Pure/stateless (src/game/RondaLogic.js's own constructor comment: "no
// specific state needed"), so one shared instance is safe — used here only
// to recompute which center cards a COLLECT matched (RONDA_ONLINE.md Fase
// 9b: CARD_PLAYED tells us the card and the outcome, not which specific
// center cards were involved).
const rondaLogic = new RondaLogic();

/** Same team split as offline Ronda.jsx everywhere (players/seats 0,2 = team1; 1,3 = team2). */
const seatTeam = (seat) => (seat % 2 === 0 ? 1 : 2);

/**
 * The real online Ronda table (RONDA_ONLINE.md Fase 9b), replacing
 * OnlinePlayPlaceholder. Renders via OnlineTableRenderer (a fork of the
 * offline DealManagerRenderer — see that file's doc comment for why),
 * driven entirely by the WebSocket protocol: GAME_STATE_SYNC for
 * authoritative state, CARD_PLAYED for exactly what to animate for each
 * individual play (mine or an opponent's/AI's), one pair per real card play
 * since the Fase 9b backend change.
 */
export default function OnlineRondaScreen({ onRoundTransition, spectator = false }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { roomId } = useParams();
    const { authFetch, getAccessToken, user } = useAuth();
    const { teamColors } = useTeamColors();
    const isMobile = useIsMobileDevice();
    // Force landscape orientation - lock orientation while in the game screen
    const { needsRotation } = useLandscapeOrientation(null, { lockOrientation: true });

    const [connectionStatus, setConnectionStatus] = useState('connecting'); // connecting | connected | reconnecting
    const [reconnectAttempt, setReconnectAttempt] = useState(0);
    const [onlineSettingsOpen, setOnlineSettingsOpen] = useState(false);
    const [scores, setScores] = useState({ 1: 0, 2: 0 });
    /** Header's RetroScoreboard (games won within the match, `rooms.match_score_team1/2` — matchTransition.ts), NOT the current game's 0-18 tally that `scores`/team1Marker/team2Marker track. Seeded from the room fetch in handlePixiMount, kept live by ROUND_TRANSITION/MATCH_OVER's `matchScore` payload — see offline Ronda.jsx's globalMatchScore for the same distinction. */
    const [matchScore, setMatchScore] = useState({ team1: 0, team2: 0 });
    const [fatalError, setFatalError] = useState(null);
    /** The match continues into a fresh round once acked/the deadline passes (matchTransition.ts) — non-null exactly while that hand-off is pending. */
    const [roundTransition, setRoundTransition] = useState(null);
    const [hasAckedTransition, setHasAckedTransition] = useState(false);
    /** The whole match just ended — terminal, no further round to hand off to. */
    const [matchOver, setMatchOver] = useState(null);
    /** RONDA_ONLINE.md Fase 11 — null except while a Ronda/Rondín cantar/envío decision is pending. Mirrors GAME_STATE_SYNC's declarationPhase field verbatim (already privacy-filtered server-side). */
    const [declarationPhase, setDeclarationPhase] = useState(null);
    const [processingDecision, setProcessingDecision] = useState(false);
    /** Contra-caída decision window (TODO.md #2) — null except while a caída/contracaída is pending a decision. Mirrors GAME_STATE_SYNC's caidaOpportunity field verbatim. */
    const [caidaOpportunity, setCaidaOpportunity] = useState(null);
    const [processingCaidaDecision, setProcessingCaidaDecision] = useState(false);
    /** Countdown shown by CaidaOpportunityButtons while it's MY seat being asked — same local setInterval-off-an-absolute-deadline pattern as RoundTransitionModal's ackDeadlineAt countdown. */
    const [caidaTimeRemaining, setCaidaTimeRemaining] = useState(null);
    /** Header's on-table retro scoreboard (src/components/Header.jsx) — same two ScoreMarker instances offline uses, fed from the server's authoritative `scores` on every sync instead of local addPoints() calls. */
    const [team1Marker] = useState(() => new ScoreMarker(1, 'oros', 8));
    const [team2Marker] = useState(() => new ScoreMarker(2, 'oros', 9));
    /** Offline parity (RondaHandSummaryModal) — non-null exactly while showing the blocking card-count summary for a deck exhaustion (never for a same-deck internal re-deal, which offline itself shows nothing for). */
    const [handSummaryData, setHandSummaryData] = useState(null);
    /** Offline parity (ShufflingModal) — the dealer to show while "shuffling", or null. Shown once per fresh mount (match start / round transition, mirroring dealInitial) and again after the hand-summary Continue when a deck exhaustion redeals mid-round. */
    const [shufflingDealer, setShufflingDealer] = useState(null);
    /** Mirrors wsClientRef in state — wsClientRef is only ever read inside stable callbacks (see enqueue's own rationale), but useRoomChat's effect needs to reactively re-subscribe once the client actually exists, which a ref alone can't trigger (same dual-tracking pattern as roundTransitionRef/roundTransition below). */
    const [wsClient, setWsClient] = useState(null);

    const appRef = useRef(null);
    const rendererRef = useRef(null);
    const toastManagerRef = useRef(null);
    const wsClientRef = useRef(null);
    const mySeatRef = useRef(null);
    const playersBySeatRef = useRef(null);
    const hasDealtRef = useRef(false);
    /** Set from CARD_PLAYED.handEnded (see handleCardPlayedEvent) to the `handTransition` info itself (null otherwise) — the very next GAME_STATE_SYNC already reflects the round's automatically re-dealt next hand (RONDA_ONLINE.md TODO #3), so it needs a re-deal animation instead of the normal no-op path, and `deckExhausted`/`roundOverAfter` say which one. Unlike Báciga's per-sync hand-signature comparison, Ronda can't use "did the card count change" as that signal: a normal play always shrinks somebody's hand by exactly one card, every single turn — only CARD_PLAYED itself knows which particular play was the one that emptied every hand. */
    const pendingHandTransitionRef = useRef(null);
    /** Read directly in the JSX below (same pattern as mySeatRef/playersBySeatRef) — Header's dealer/turn info doesn't need its own re-render trigger since a state sync always also calls setScores, which already re-renders this component. */
    const dealerIndexRef = useRef(null);
    const currentTurnRef = useRef(null);
    const myPendingCardRef = useRef(null);
    /** Set when the connection comes back up after having already dealt once — the next GAME_STATE_SYNC has no CARD_PLAYED before it (nothing was "just played"), so it needs a full instant re-render instead of the normal incremental path (Fase 9c). */
    const pendingResyncRef = useRef(false);
    const queueRef = useRef(Promise.resolve());
    const unmountedRef = useRef(false);
    /** Mirrors the `roundTransition` state for onConnectionChange's closure (set up once in handlePixiMount, deliberately not re-created — see its own eslint-disable comment — so it can't read fresh state directly). */
    const roundTransitionRef = useRef(null);
    /** True once ROUND_TRANSITION or MATCH_OVER has been received — the next socket close is the server's own doing (the next round is already dealt, or the match is over), not a real disconnect GameWsClient should retry into a now-stale or nonexistent session. */
    const expectingTransitionCloseRef = useRef(false);
    /** Resolves the Promise handleCardPlayedEvent is awaiting on while the hand-summary modal is open — set right before showing it, called by handleHandSummaryContinue. */
    const handSummaryContinueResolveRef = useRef(null);
    /** The currently-deferred caída card (its seat, {suit,value}, and the sprite already dropped into the center by applyCardPlayed) — set whenever a CARD_PLAYED opens/re-opens a caída opportunity, read by the RONDA_CAIDA_DECLINED handler to finish that collector's sweep. Always points at the LATEST (deepest) pending level, since a decline can only ever resolve the currently-open one. */
    const pendingCaidaCardRef = useRef(null);

    /**
     * Runs `fn` after every previously queued message has finished
     * processing — keeps CARD_PLAYED animations and the GAME_STATE_SYNC
     * that follows each one strictly in order, regardless of how fast they
     * arrive over the wire. A failure in one queued step is logged and
     * swallowed rather than left as an unhandled rejection that would
     * otherwise also break every later message's chain.
     */
    const enqueue = useCallback((fn) => {
        queueRef.current = queueRef.current.then(fn, fn).catch((err) => {
            console.error('[OnlineRondaScreen] Error processing a queued message:', err);
        });
        return queueRef.current;
    }, []);

    /** {name, avatar} for Header/ShufflingModal/RondaHandSummaryModal — reads playersBySeatRef (populated once in handlePixiMount, before any of these ever needs it). */
    const toHeaderPlayer = useCallback((seat) => {
        const player = playersBySeatRef.current?.[seat];
        if (!player) return null;
        return { name: player.name, avatar: player.avatar || (player.isSystem ? '🤖' : '🙂') };
    }, []);

    /** Offline parity (Ronda.jsx's inline caidaIcon ternaries) — same icon per caída level. */
    const caidaIconFor = (caidaType) =>
        caidaType === 'Contracaída' ? TOAST_ICONS.CAIDA_4 : caidaType === 'San Vicente' ? '💫' : TOAST_ICONS.CAIDA_3;

    /** Offline parity (Ronda.jsx's Aseo/Caída toasts, e.g. handleCardPlay's COLLECT branch) — shared by a normal play and a caída decision's deferred sweep (handleCaidaDeclinedEvent). */
    const showCaidaAseoToast = useCallback((seat, caidaType, isAseo) => {
        if (!caidaType && !isAseo) return;
        const team = seatTeam(seat);
        const teamColor = team === 1 ? teamColors.team1.hex : teamColors.team2.hex;
        let message;
        let duration = 2000;
        if (caidaType && isAseo) {
            message = `${TOAST_ICONS.ASEO} Aseo + ${caidaIconFor(caidaType)} ${caidaType}`;
            duration = 3000;
        } else if (caidaType) {
            message = `${caidaIconFor(caidaType)} ${caidaType}`;
        } else {
            message = `${TOAST_ICONS.ASEO} Aseo`;
        }
        toastManagerRef.current?.showPlayerToast(message, toVisualSlot(seat, mySeatRef.current), { type: 'team' + team, teamColor, duration });
    }, [teamColors]);

    /** Offline parity (Ronda.jsx's handleCardPlay Media/Además toasts and checkAndResolveEnvioAfterMedias's win toast). */
    const showDeclarationRevealToast = useCallback((seat, card, declarationReveal) => {
        if (!declarationReveal) return;
        const cardName = rondaLogic.getCardValueName(card.value);
        if (declarationReveal.kind === 'media') {
            const mediaMessage = declarationReveal.isLastMedia ? 'La otra media' : 'Media';
            toastManagerRef.current?.showPlayerToast(`${TOAST_ICONS.MEDIA} ${mediaMessage}${SEPARATOR}${cardName}`, toVisualSlot(seat, mySeatRef.current), { type: 'warning', duration: 2500 });
            if (declarationReveal.resolved) {
                const { winningTeam, totalPoints, declarationType, cardValue, resolutionType } = declarationReveal.resolved;
                const typeName = declarationType === 'rondin' ? 'Rondín' : 'Ronda';
                const winCardName = rondaLogic.getCardValueName(cardValue);
                const messagePrefix = resolutionType === 'a_la_mayor' ? 'A la mayor:' : '¡Envío ganado!';
                const winningTeamColor = winningTeam === 1 ? teamColors.team1.hex : teamColors.team2.hex;
                toastManagerRef.current?.showToast(
                    `${messagePrefix}\n${TOAST_ICONS.WINNER} ${formatTeam(winningTeam)}${SEPARATOR}${typeName} de ${winCardName}${SEPARATOR}${formatPoints(totalPoints)}`,
                    { type: 'team' + winningTeam, teamColor: winningTeamColor, duration: 4000, position: 'center' }
                );
            }
        } else if (declarationReveal.kind === 'ademas') {
            toastManagerRef.current?.showPlayerToast(`${TOAST_ICONS.CARD} Además${SEPARATOR}${cardName}`, toVisualSlot(seat, mySeatRef.current), { type: 'info', duration: 2500 });
        }
    }, [teamColors]);

    const handleMyCardClick = useCallback((cardData, sprite) => {
        const seat = mySeatRef.current;
        // Reset the hover scale/lift (pointerout never fires — the card
        // leaves the hand instead of the pointer leaving the sprite) before
        // it animates to the center, same fix as offline Ronda.jsx's
        // handleCardPlay.
        if (sprite._originalScale) {
            sprite.scale.set(sprite._originalScale);
            sprite.y = sprite._originalY;
            sprite.zIndex = 0;
        }
        rendererRef.current?.disablePlayerCardsInteractivity(seat);
        myPendingCardRef.current = sprite;
        wsClientRef.current?.send({
            type: 'PLAY_CARD',
            actionId: crypto.randomUUID(),
            payload: { suit: cardData.suit, value: cardData.value },
        });
    }, []);

    const applyCardPlayed = useCallback(async (payload) => {
        const { seat, card, action, caidaType, isAseo, declarationReveal, caidaOpportunity: openedOpportunity } = payload;
        const renderer = rendererRef.current;
        if (!renderer) return;

        // Offline parity (GAME_TIMINGS.ai.thinkingTime): the server resolves
        // an AI seat's move instantly, so without this the card would just
        // snap onto the table the moment it's played — a real human seat
        // (online opponent) is never artificially delayed, only SYSTEM ones.
        // A spectator owns no seat: every card, seat 0's included, is a
        // face-down placeholder to reveal, never a sprite the viewer clicked.
        const isMine = !spectator && seat === mySeatRef.current;
        if (!isMine && playersBySeatRef.current?.[seat]?.isSystem) {
            await new Promise((resolve) => setTimeout(resolve, GAME_TIMINGS.ai.thinkingTime));
            if (unmountedRef.current || rendererRef.current !== renderer) return;
        }

        let sprite;
        if (isMine) {
            sprite = myPendingCardRef.current;
            myPendingCardRef.current = null;
        } else {
            // A face-down placeholder from that seat's hand — reveal it now
            // that the server has told everyone exactly which card it was.
            sprite = renderer.getPlayerCardSprites(seat)[0];
        }
        if (!sprite) return; // defensive — shouldn't happen if state stayed in sync
        sprite.cardData = card;

        // Contra-caída decision window (TODO.md #2): this exact play just
        // opened (or re-opened, one level deeper) a caída/contracaída
        // opportunity — nothing is final yet (points/caidaType/isAseo are
        // already 0/null/false from the server), so only animate the card
        // joining the center, same as a DROP. The RONDA_CAIDA_DECLINED
        // handler below finishes the deferred sweep if it's later declined;
        // if it's instead accepted, the NEXT CARD_PLAYED naturally sweeps
        // everything still sitting here (this sprite included) via the
        // normal COLLECT branch below — RondaLogic.processMove recomputes
        // matches fresh from whatever's actually in the center each time,
        // so no special-casing is needed there regardless of how many
        // levels deep the chain has gone.
        if (action === 'DROP' || openedOpportunity) {
            await renderer.animateCardDrop(seat, sprite);
            if (openedOpportunity) pendingCaidaCardRef.current = { collectorSeat: seat, card, sprite };
        } else {
            const currentCenterData = renderer.getCenterCardSprites().map((s) => s.cardData);
            const result = rondaLogic.processMove(card, currentCenterData);
            const matchedCards = (result.centerCardsToRemove || [])
                .map((matchedCard) => ({
                    card: matchedCard,
                    sprite: renderer
                        .getCenterCardSprites()
                        .find((s) => s.cardData?.suit === matchedCard.suit && s.cardData?.value === matchedCard.value),
                }))
                .filter((m) => m.sprite);

            await renderer.animateSequentialCollection(sprite, matchedCards, renderer.getPilePosition(seat));
            matchedCards.forEach((m) => m.sprite.destroy());
            if (!sprite.destroyed) sprite.destroy();
        }

        // caidaType is already a display-ready Spanish term from the engine
        // (RondaLogic.getCaidaResult: 'Caída' | 'Contracaída' | 'San Vicente'),
        // same as what the offline screen shows — not an i18n key.
        if (caidaType) soundVibrationService.triggerCaida();
        showCaidaAseoToast(seat, caidaType, isAseo);

        // RONDA_ONLINE.md Fase 11: this exact play revealed (or didn't) part
        // of `seat`'s announced Ronda/Rondín. `resolved` — only present once
        // enough medias are revealed — is the moment a deferred envío
        // (aceptado/a la mayor) actually pays out; scores were already
        // updated server-side, this is purely the toast.
        showDeclarationRevealToast(seat, card, declarationReveal);
    }, [showCaidaAseoToast, showDeclarationRevealToast, spectator]);

    const handleGameStateSync = useCallback((payload, isReconnect = false) => enqueue(async () => {
        if (unmountedRef.current) return;

        if (!hasDealtRef.current) {
            // A spectator has no seat (payload.seat is null) — only a camera
            // angle (payload.viewpointSeat), used purely to orient the table.
            const viewpointSeat = payload.seat ?? payload.viewpointSeat;
            mySeatRef.current = viewpointSeat;
            const renderer = new OnlineTableRenderer(appRef.current, viewpointSeat);
            rendererRef.current = renderer;
            // Offline parity (Ronda.jsx) — without this, mobile always got
            // the fixed 100x150 desktop card size, since this renderer never
            // read the device type on its own.
            const screenWidth = appRef.current.screen.width;
            const cardWidth = isMobile ? Math.floor(screenWidth * 0.06) : 100;
            const cardHeight = isMobile ? Math.floor(cardWidth * 1.5) : 150;
            renderer.setCardSize(cardWidth, cardHeight);
            if (playersBySeatRef.current) renderer.setPlayerNames(playersBySeatRef.current);
            renderer.setScoreMarkers(team1Marker, team2Marker);
            if (isReconnect) {
                // TODO.md (leaving/re-entering the app mid-game always showed
                // "shuffling") — this fresh mount is rejoining a hand already
                // in progress (the server says so via GAME_STATE_SYNC's
                // isReconnect), not the true start of a deal: snap straight
                // to the current state instead of animating a deal that
                // already happened, possibly hands ago.
                await renderer.renderState(payload);
            } else {
                // Offline parity (ShufflingModal) — a genuine fresh mount is
                // either a brand new match (right after the cut-the-deck
                // ceremony) or a round transition into this game type;
                // offline always shows a beat of "shuffling" before the very
                // first deal in both cases.
                setShufflingDealer(toHeaderPlayer(payload.dealerIndex));
                await new Promise((resolve) => setTimeout(resolve, GAME_TIMINGS.dealing.shufflingModal));
                setShufflingDealer(null);
                if (unmountedRef.current) return;
                await renderer.dealInitial(payload);
            }
            hasDealtRef.current = true;
        } else if (pendingResyncRef.current) {
            // A reconnection resync always wins over a pending hand
            // transition (both could in principle line up if the socket
            // dropped mid-transition): after a reconnect the client has no
            // idea what was "just dealt" to animate off of, so it must snap
            // straight to whatever the server says is true right now.
            pendingResyncRef.current = false;
            pendingHandTransitionRef.current = null;
            // A sprite we were waiting to confirm (our own in-flight play)
            // no longer corresponds to anything real after a full rebuild.
            myPendingCardRef.current = null;
            await rendererRef.current?.renderState(payload);
        } else if (pendingHandTransitionRef.current) {
            // RONDA_ONLINE.md TODO #3: pendingHandTransitionRef is set by
            // handleCardPlayedEvent when the CARD_PLAYED that immediately
            // preceded this sync was the one that emptied every hand — the
            // round auto-redealt before this sync was even built, so it's
            // already the fresh next hand's state, not the finished one.
            // Offline parity: which re-deal animation plays depends on
            // whether the same deck kept going or got reshuffled.
            const transition = pendingHandTransitionRef.current;
            pendingHandTransitionRef.current = null;
            myPendingCardRef.current = null;
            if (!transition.deckExhausted) {
                // Same-deck internal re-deal (offline: DealManager.dealHand
                // (dealer, false) + animateDealSubsequentHand) — 12 more
                // cards fly out to the 4 hands, center cards untouched.
                await rendererRef.current?.animateDealSubsequentHand(payload);
                // Offline parity (Ronda.jsx isPenultimateHand): the deck was
                // just dealt down to 0, so this dealer's next hand (once this
                // one plays out) forces the reshuffle/dealer rotation — warn
                // the table this was the last deal of the dealer's turn.
                if (payload.deckRemaining === 0 && toastManagerRef.current) {
                    toastManagerRef.current.showToast(`${TOAST_ICONS.SE_HAN_IDO} Se han ido${SEPARATOR}${TOAST_ICONS.LAST_HAND} Última mano`, {
                        type: 'info',
                        duration: 3000,
                        position: 'center'
                    });
                }
            } else if (!transition.roundOverAfter) {
                // Deck just ran out mid-round (nobody hit 18 yet) — a fresh
                // shuffled deck deals a brand new hand with center cards too,
                // same animation as the very first hand of the round
                // (offline: dealHand(dealer, true) + animateDealFirstHand).
                await rendererRef.current?.dealInitial(payload);
            } else {
                // The round just ended on the arrastre itself — nothing left
                // to deal here (ROUND_TRANSITION/MATCH_OVER follows shortly).
                // Snap to the now-empty final state, same as offline shows
                // nothing extra in this case either.
                await rendererRef.current?.renderState(payload);
            }
        }

        dealerIndexRef.current = payload.dealerIndex;
        currentTurnRef.current = payload.currentTurn;
        setScores(payload.scores);
        setDeclarationPhase(payload.declarationPhase);
        setProcessingDecision(false);
        setCaidaOpportunity(payload.caidaOpportunity);
        setProcessingCaidaDecision(false);
        team1Marker.setScore(payload.scores[1]);
        team2Marker.setScore(payload.scores[2]);
        rendererRef.current?.updateScoreMarkers();
        await rendererRef.current?.updateDeck(payload.dealerIndex, payload.deckRemaining);

        if (payload.roundOver) {
            // Terminal for this ROUND, not necessarily the match — a
            // ROUND_TRANSITION (continue into a fresh round) or MATCH_OVER
            // event follows shortly after (matchTransition.ts), handled by
            // their own listeners below.
            rendererRef.current?.disablePlayerCardsInteractivity(mySeatRef.current);
        } else if (!spectator && !payload.declarationPhase && !payload.caidaOpportunity && payload.currentTurn === mySeatRef.current) {
            rendererRef.current?.makePlayerCardsInteractive(mySeatRef.current, handleMyCardClick);
        } else {
            // Also covers a pending Ronda/Rondín decision (own or another
            // seat's) and a pending caída/contracaída decision (TODO.md #2,
            // own or another seat's) — card play stays blocked until it's
            // settled, same as the server enforces (DECLARATION_PENDING /
            // CAIDA_OPPORTUNITY_PENDING).
            rendererRef.current?.disablePlayerCardsInteractivity(mySeatRef.current);
        }
    }), [enqueue, handleMyCardClick, team1Marker, team2Marker, toHeaderPlayer, isMobile, spectator]);

    /**
     * A same-deck internal re-deal (deckExhausted: false) shows no modal,
     * exactly like offline's own startNewRound() "else" branch — the re-deal
     * animation itself (handleGameStateSync's animateDealSubsequentHand call)
     * is the only feedback. Only a deck exhaustion gets offline's blocking
     * RondaHandSummaryModal (card counts + arrastre points), and — unless the
     * arrastre itself just ended the round — the ShufflingModal beat for the
     * freshly reshuffled deck (whose own full re-deal animation likewise
     * happens back in handleGameStateSync, via dealInitial). Shared by
     * handleCardPlayedEvent and handleCaidaDeclinedEvent below — either a
     * normal play or a declined caída opportunity can be the one that empties
     * every hand (RONDA_ONLINE.md TODO #3).
     */
    const runHandTransitionIfNeeded = useCallback(async (transition) => {
        if (!transition?.deckExhausted) {
            // Offline parity (Ronda.jsx: GAME_TIMINGS.dealing.beforeNewHand)
            // — a small breather before the same-deck re-deal animation
            // starts, instead of the queue moving straight into it.
            await new Promise((resolve) => setTimeout(resolve, GAME_TIMINGS.dealing.beforeNewHand));
            return;
        }
        await new Promise((resolve) => {
            handSummaryContinueResolveRef.current = resolve;
            setHandSummaryData({
                team1Cards: transition.collectedCounts[1],
                team2Cards: transition.collectedCounts[2],
                team1Points: transition.arrastrePoints[1],
                team2Points: transition.arrastrePoints[2],
            });
        });
        if (unmountedRef.current) return;
        if (!transition.roundOverAfter) {
            setShufflingDealer(toHeaderPlayer(transition.newDealerIndex));
            await new Promise((resolve) => setTimeout(resolve, GAME_TIMINGS.dealing.shufflingModal));
            if (unmountedRef.current) return;
            setShufflingDealer(null);
        }
    }, [toHeaderPlayer]);

    const handleCardPlayedEvent = useCallback((payload) => enqueue(async () => {
        await applyCardPlayed(payload);
        // Read after the animation, not before: the flag only needs to be
        // set before the NEXT enqueued task (this play's own
        // GAME_STATE_SYNC) runs, and the animation above needs the current
        // (pre-transition) sprites, unaffected either way by when this runs.
        // Both awaits inside runHandTransitionIfNeeded hold up this queued
        // task, which in turn (via `enqueue`) naturally delays the next
        // queued task — this play's own GAME_STATE_SYNC — until the player
        // has dismissed them, same as offline pausing the whole hand there.
        if (payload.handEnded) {
            pendingHandTransitionRef.current = payload.handTransition;
            await runHandTransitionIfNeeded(payload.handTransition);
        }
    }), [enqueue, applyCardPlayed, runHandTransitionIfNeeded]);

    /**
     * Contra-caída decision window (TODO.md #2): the awaiting seat declined
     * ("pasar"), so the ORIGINAL collector's deferred sweep — every card
     * that's been sitting un-collected in the center since the opportunity
     * opened, tracked via pendingCaidaCardRef — finally completes. Mirrors
     * applyCardPlayed's own COLLECT branch, just sourced from the already-
     * dropped deferred sprite instead of a freshly played one, and excluding
     * that sprite itself from the "other center cards" passed to
     * rondaLogic.processMove (it would otherwise match against itself).
     */
    const handleCaidaDeclinedEvent = useCallback((payload) => enqueue(async () => {
        if (unmountedRef.current) return;
        const { collectorSeat, caidaType, isAseo, handEnded, handTransition } = payload;
        const renderer = rendererRef.current;
        const deferred = pendingCaidaCardRef.current;
        pendingCaidaCardRef.current = null;
        setCaidaOpportunity(null);
        setProcessingCaidaDecision(false);

        if (renderer && deferred) {
            const allCenterSprites = renderer.getCenterCardSprites();
            const deferredSprite =
                deferred.sprite && !deferred.sprite.destroyed
                    ? deferred.sprite
                    : allCenterSprites.find(
                          (s) => s.cardData?.suit === deferred.card.suit && s.cardData?.value === deferred.card.value
                      );
            if (deferredSprite) {
                const otherCenterData = allCenterSprites.filter((s) => s !== deferredSprite).map((s) => s.cardData);
                const result = rondaLogic.processMove(deferred.card, otherCenterData);
                const matchedCards = (result.centerCardsToRemove || [])
                    .map((matchedCard) => ({
                        card: matchedCard,
                        sprite: allCenterSprites.find(
                            (s) => s !== deferredSprite && s.cardData?.suit === matchedCard.suit && s.cardData?.value === matchedCard.value
                        ),
                    }))
                    .filter((m) => m.sprite);

                await renderer.animateSequentialCollection(deferredSprite, matchedCards, renderer.getPilePosition(collectorSeat));
                matchedCards.forEach((m) => m.sprite.destroy());
                if (!deferredSprite.destroyed) deferredSprite.destroy();
            }
        }

        if (caidaType) soundVibrationService.triggerCaida();
        showCaidaAseoToast(collectorSeat, caidaType, isAseo);

        if (handEnded) {
            pendingHandTransitionRef.current = handTransition;
            await runHandTransitionIfNeeded(handTransition);
        }
    }), [enqueue, showCaidaAseoToast, runHandTransitionIfNeeded]);

    const handleHandSummaryContinue = useCallback(() => {
        setHandSummaryData(null);
        handSummaryContinueResolveRef.current?.();
        handSummaryContinueResolveRef.current = null;
    }, []);

    const handleDeclarationEvent = useCallback((payload) => enqueue(async () => {
        if (unmountedRef.current) return;
        const { seat, decision, declarationType, pointsAwarded, awardedTeam } = payload;

        // Offline parity (GAME_TIMINGS.ronda.betweenAnnouncements): a beat
        // between consecutive SYSTEM seats' declarations instead of a burst
        // of toasts appearing all at once — a real human seat is never
        // artificially delayed.
        if (playersBySeatRef.current?.[seat]?.isSystem) {
            await new Promise((resolve) => setTimeout(resolve, GAME_TIMINGS.ronda.betweenAnnouncements));
            if (unmountedRef.current) return;
        }

        const team = seatTeam(seat);
        const teamColor = team === 1 ? teamColors.team1.hex : teamColors.team2.hex;

        // Offline parity (Ronda.jsx's declaration-announce toasts and
        // processEnvioForAI's per-decision switch) — same icon/text per
        // decision, team-colored except a plain "Paso".
        if (decision === 'cantar') {
            const typeName = declarationType === 'rondin' ? 'Rondín' : 'Ronda';
            toastManagerRef.current?.showPlayerToast(`${TOAST_ICONS.DECLARATION} ${typeName}`, toVisualSlot(seat, mySeatRef.current), { type: 'team' + team, teamColor, duration: 3000 });
        } else if (decision === 'no_cantar') {
            toastManagerRef.current?.showPlayerToast(`${TOAST_ICONS.PASO} Paso`, toVisualSlot(seat, mySeatRef.current), { type: 'info', duration: 2500 });
        } else {
            const decisionMessages = {
                enviar: `${TOAST_ICONS.ENVIO} Envío`,
                chiquita: `${TOAST_ICONS.CHIQUITA} Chiquita`,
                acepto: `${TOAST_ICONS.ACCEPT} Acepto`,
                no_acepto: `${TOAST_ICONS.REJECT} No acepto`,
                a_la_mayor: `${TOAST_ICONS.A_LA_MAYOR} A la mayor`,
            };
            if (decisionMessages[decision]) {
                toastManagerRef.current?.showPlayerToast(decisionMessages[decision], toVisualSlot(seat, mySeatRef.current), { type: 'team' + team, teamColor, duration: 3500 });
            }
        }

        // Offline parity — accepting or deferring to "a la mayor" shows an
        // immediate centered banner; the actual payout toast only comes
        // later, once enough medias are revealed (showDeclarationRevealToast
        // via applyCardPlayed's declarationReveal.resolved).
        if (decision === 'acepto') {
            toastManagerRef.current?.showToast(`${TOAST_ICONS.ACCEPT} Envío aceptado${SEPARATOR}Se determina al ver las medias`, {
                type: 'info', duration: 4000, position: 'center',
            });
        } else if (decision === 'a_la_mayor') {
            toastManagerRef.current?.showToast(`${TOAST_ICONS.A_LA_MAYOR} A la mayor${SEPARATOR}Se determina al ver las medias`, {
                type: 'info', duration: 4000, position: 'center',
            });
        }

        // Offline parity — an immediate payout: either a declined envío
        // handing the sender's own points to their team (Ronda.jsx's
        // resolveEnvio), or just one team having announced at all
        // (Ronda.jsx's awardRondaPoints) — the two use different wording.
        if (pointsAwarded > 0 && awardedTeam) {
            const awardedTeamColor = awardedTeam === 1 ? teamColors.team1.hex : teamColors.team2.hex;
            const message = decision === 'no_acepto'
                ? `${TOAST_ICONS.REJECT} Envío rechazado\n${formatTeam(awardedTeam)}${SEPARATOR}${formatPoints(pointsAwarded)}`
                : `${TOAST_ICONS.TEAM_SCORE} ${formatTeam(awardedTeam)}${SEPARATOR}${formatPoints(pointsAwarded)}`;
            toastManagerRef.current?.showToast(message, {
                type: 'team' + awardedTeam,
                teamColor: awardedTeamColor,
                duration: decision === 'no_acepto' ? 3000 : 2500,
                position: 'center',
            });
        }
    }), [enqueue, teamColors]);

    const handleSeatEvent = useCallback((seat, status) => {
        rendererRef.current?.setSeatDimmed(seat, status !== 'reconnected');
        const key =
            status === 'disconnected'
                ? 'online.play.seat_disconnected'
                : status === 'substituted_inactivity'
                  ? 'online.play.seat_substituted_inactivity'
                  : status === 'substituted'
                    ? 'online.play.seat_substituted'
                    : 'online.play.seat_reconnected';
        toastManagerRef.current?.showPlayerToast(t(key), toVisualSlot(seat, mySeatRef.current), {
            type: status === 'reconnected' ? 'success' : 'warning',
            duration: 3000,
        });
    }, [t]);

    /**
     * A round win, match not over yet (matchTransition.ts) — the server
     * keeps this session's sockets open just long enough to collect
     * ROUND_CONTINUE_ACKs (or hit the deadline), then deals the next round
     * and closes them; see the onConnectionChange handling below for what
     * happens once that close arrives.
     *
     * TODO.md (OnlineBacigaScreen.jsx's identical fix — "un jugador de la
     * IA se hizo las cosas... el juego se me bloqueó") —
     * expectingTransitionCloseRef/roundTransitionRef used to be set INSIDE
     * the enqueue()'d callback, alongside the modal's own setRoundTransition.
     * A hand that ends via a long chained cascade (a deep caída chain, each
     * collector paced for the eye) can take longer to drain through
     * enqueue() than matchTransition.ts's own ackDeadlineMs (10s) fallback —
     * the server closes THIS socket for the transition (ack or timeout,
     * whichever comes first) while the client is still busy animating
     * everything that led up to it, so the close arrives before this
     * handler ever runs and expectingTransitionCloseRef is still false.
     * onConnectionChange then can't tell that close apart from a real
     * disconnect, GameWsClient auto-reconnects into the new round on its
     * own (correctly resyncing the board), and by the time enqueue()
     * finally reaches this handler, it opens the round-transition modal for
     * a transition that already fully happened — a modal now stuck
     * forever, since nothing will ever again call onRoundTransition() to
     * close it. Setting both refs synchronously, the instant the message
     * arrives (before enqueue(), not inside it), guarantees they're true
     * before any close this message could possibly cause, no matter how
     * backlogged the animation queue is — only the user-visible modal
     * itself stays queued, so it still waits its turn instead of
     * interrupting whatever's mid-animation.
     */
    const handleRoundTransitionEvent = useCallback((payload) => {
        expectingTransitionCloseRef.current = true;
        roundTransitionRef.current = payload;
        return enqueue(async () => {
            if (unmountedRef.current) return;
            setHasAckedTransition(false);
            setRoundTransition(payload);
            if (payload.matchScore) setMatchScore({ team1: payload.matchScore[1], team2: payload.matchScore[2] });
        });
    }, [enqueue]);

    /** The whole match just ended — no next round to hand off to, so close proactively rather than wait for the server (which doesn't send a follow-up close for this case the way a round transition does). */
    const handleMatchOverEvent = useCallback((payload) => {
        // Same reasoning as handleRoundTransitionEvent above.
        expectingTransitionCloseRef.current = true;
        return enqueue(async () => {
            if (unmountedRef.current) return;
            wsClientRef.current?.close();
            setMatchOver(payload);
            if (payload.matchScore) setMatchScore({ team1: payload.matchScore[1], team2: payload.matchScore[2] });
        });
    }, [enqueue]);

    const handleRoundTransitionContinue = useCallback(() => {
        setHasAckedTransition(true);
        wsClientRef.current?.send({ type: 'ROUND_CONTINUE_ACK' });
    }, []);

    // Spectators have no chat (they aren't in the room) — keeps the hook from
    // even trying the room's chat endpoint.
    const chat = useRoomChat({ roomId, authFetch, wsClient, userId: user?.id, enabled: !spectator });
    const chatToggle = useChatUnread(roomId, chat.messages);

    /**
     * TODO.md idea: float a quick-reaction emoji over the sending player's
     * seat when it arrives in chat. `null` until the first message list is
     * seen, so the initial REST backfill (waiting-room history, or a
     * reconnect's) never replays old reactions — only messages that show up
     * AFTER that point are new arrivals worth animating.
     */
    const seenChatMessageIdsRef = useRef(null);
    useEffect(() => {
        if (seenChatMessageIdsRef.current === null) {
            seenChatMessageIdsRef.current = new Set(chat.messages.map((m) => m.id));
            return;
        }
        for (const message of chat.messages) {
            if (seenChatMessageIdsRef.current.has(message.id)) continue;
            seenChatMessageIdsRef.current.add(message.id);
            if (message.kind === 'emote' && message.senderSeat !== null && message.senderSeat !== undefined) {
                const emoji = EMOTE_PRESETS.find((e) => e.slug === message.body)?.emoji;
                if (emoji) toastManagerRef.current?.showPlayerReaction(emoji, toVisualSlot(message.senderSeat, mySeatRef.current));
            }
        }
    }, [chat.messages]);

    useEffect(() => {
        unmountedRef.current = false;
        return () => {
            unmountedRef.current = true;
            wsClientRef.current?.close();
            toastManagerRef.current?.destroy();
            rendererRef.current?.destroy();
        };
    }, []);

    const handlePixiMount = useCallback(async (app) => {
        appRef.current = app;
        toastManagerRef.current = new ToastManager(app);
        soundVibrationService.initialize();

        try {
            // A spectator isn't in the room, so /rooms/mine has nothing for
            // them — /rooms/live/:id (public, in-progress rooms only) gives
            // the same seats/match score without any hand information.
            const room = await authFetch(spectator ? `/rooms/live/${roomId}` : '/rooms/mine');
            if (unmountedRef.current) return;
            if (room.id !== roomId) {
                navigate('/online', { replace: true });
                return;
            }
            playersBySeatRef.current = [0, 1, 2, 3].map((seat) => {
                const seatInfo = room.seats.find((s) => s.seat === seat);
                if (seatInfo?.occupantType === 'HUMAN') return { name: seatInfo.userName || '?', isSystem: false };
                if (seatInfo?.occupantType === 'SYSTEM') {
                    const persona = getAiPersona(room.id, seat);
                    return { name: `${persona.name} ${t('online.play.ai_name_suffix')}`, avatar: persona.avatar, isSystem: true };
                }
                return { name: '', isSystem: false };
            });
            setMatchScore({ team1: room.matchScoreTeam1 || 0, team2: room.matchScoreTeam2 || 0 });
        } catch {
            if (!unmountedRef.current) navigate('/online', { replace: true });
            return;
        }

        const client = new GameWsClient(roomId, getAccessToken, { spectate: spectator });
        wsClientRef.current = client;
        setWsClient(client);
        // The game ended, was cancelled, or can't be watched any more (the
        // server closes with a terminal code and a retry would never succeed).
        client.onTerminalClose = () => {
            if (!unmountedRef.current) navigate('/online', { replace: true });
        };
        client.onConnectionChange = (isConnected) => {
            if (unmountedRef.current) return;
            if (!isConnected && expectingTransitionCloseRef.current) {
                // The server closed this on purpose — either the next round
                // (possibly a different game type under CLASSIC mode) is
                // already dealt, or the whole match just ended. Stop
                // GameWsClient's own reconnect loop (it would otherwise
                // retry into a now-mismatched or nonexistent session) and
                // hand off to OnlinePlayScreen for a fresh remount, if a
                // next round is what's waiting.
                client.close();
                if (roundTransitionRef.current) onRoundTransition?.(roundTransitionRef.current.nextGameType);
                return;
            }
            setConnectionStatus(isConnected ? 'connected' : 'reconnecting');
            if (isConnected) {
                setReconnectAttempt(0);
                // Only a RECONNECT (not the very first connect) needs a
                // resync — hasDealtRef is only true once the initial deal
                // has happened.
                if (hasDealtRef.current) pendingResyncRef.current = true;
            }
        };
        client.onReconnecting = (attempt) => {
            if (!unmountedRef.current) setReconnectAttempt(attempt);
        };
        client.on('GAME_STATE_SYNC', (msg) => handleGameStateSync(msg.payload, msg.isReconnect));
        client.on('CARD_PLAYED', (msg) => handleCardPlayedEvent(msg.payload));
        client.on('RONDA_DECLARATION_EVENT', (msg) => handleDeclarationEvent(msg.payload));
        client.on('RONDA_CAIDA_DECLINED', (msg) => handleCaidaDeclinedEvent(msg.payload));
        client.on('ROUND_TRANSITION', (msg) => handleRoundTransitionEvent(msg.payload));
        client.on('MATCH_OVER', (msg) => handleMatchOverEvent(msg.payload));
        client.on('PLAYER_DISCONNECTED', (msg) => handleSeatEvent(msg.payload.seat, 'disconnected'));
        client.on('PLAYER_RECONNECTED', (msg) => handleSeatEvent(msg.payload.seat, 'reconnected'));
        client.on('PLAYER_SUBSTITUTED', (msg) =>
            handleSeatEvent(msg.payload.seat, msg.payload.reason === 'inactivity' ? 'substituted_inactivity' : 'substituted')
        );
        client.on('GAME_CANCELLED', (msg) => setFatalError(msg.payload?.reason || t('online.play.game_cancelled')));
        client.on('ERROR', (msg) => {
            toastManagerRef.current?.showToast(msg.message || t('online.errors.generic'), { type: 'error', duration: 3000 });
            // A rejected action (a stale click racing a state change,
            // NOT_YOUR_TURN, INVALID_CARD...) has no follow-up
            // GAME_STATE_SYNC of its own — without this, handleMyCardClick's
            // proactive disablePlayerCardsInteractivity (or a pending
            // RONDA_DECISION's/RONDA_CAIDA_DECISION's setProcessing*(true))
            // would never get undone, leaving the hand permanently unresponsive.
            client.send({ type: 'REQUEST_STATE' });
        });
        client.connect();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- stable refs/callbacks only, deliberately mounted once per screen instance
    }, [roomId, spectator]);

    const handleBackToLobby = useCallback(() => {
        wsClientRef.current?.close();
        navigate('/online');
    }, [navigate]);

    const handleDeclarationDecision = useCallback((decision) => {
        setProcessingDecision(true);
        wsClientRef.current?.send({
            type: 'RONDA_DECISION',
            actionId: crypto.randomUUID(),
            payload: { decision },
        });
    }, []);

    const handleCaidaDecision = useCallback((decision) => {
        setProcessingCaidaDecision(true);
        // No card in the payload — the server always picks the matching
        // card itself (RondaTable.applyCaidaDecision), exactly like offline
        // never lets the player choose a suit.
        wsClientRef.current?.send({
            type: 'RONDA_CAIDA_DECISION',
            actionId: crypto.randomUUID(),
            payload: { decision },
        });
    }, []);

    const isMyCaidaOpportunity = !spectator && caidaOpportunity?.awaitingSeat === mySeatRef.current;

    // Countdown for CaidaOpportunityButtons — same local setInterval-off-an-
    // absolute-deadline pattern as RoundTransitionModal's ackDeadlineAt.
    useEffect(() => {
        const deadlineAt = isMyCaidaOpportunity ? caidaOpportunity.deadlineAt : null;
        if (!deadlineAt) {
            setCaidaTimeRemaining(null);
            return;
        }
        const tick = () => setCaidaTimeRemaining(Math.max(0, Math.ceil((deadlineAt - Date.now()) / 1000)));
        tick();
        const interval = setInterval(tick, 250);
        return () => clearInterval(interval);
    }, [isMyCaidaOpportunity, caidaOpportunity]);

    // CSS styles for rotation fallback when the native/Web Orientation lock fails (web only)
    const rotationWrapperStyle = needsRotation ? {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vh',
        height: '100vw',
        transform: 'rotate(90deg)',
        transformOrigin: 'top left',
        marginLeft: '100vw',
        overflow: 'hidden',
    } : {};

    return (
        <Box sx={rotationWrapperStyle}>
            <Header
                title={t('online.play.title_with_game', { game: t('online.lobby.game_type_ronda') })}
                showSettings={false}
                onBack={handleBackToLobby}
                globalScore={matchScore}
                players={playersBySeatRef.current}
                dealer={toHeaderPlayer(dealerIndexRef.current)}
                player={toHeaderPlayer(currentTurnRef.current)}
                onToggleChat={spectator ? undefined : chatToggle.toggle}
                chatUnreadCount={chatToggle.unreadCount}
                onOnlineSettings={spectator ? undefined : () => setOnlineSettingsOpen(true)}
            />

            {spectator && (
                <Alert severity="info" icon={<VisibilityIcon fontSize="inherit" />} sx={{ position: 'fixed', top: 56, left: 0, right: 0, zIndex: 1900, py: 0 }}>
                    {t('online.spectate.banner')}
                </Alert>
            )}

            <OnlineSettingsModal
                open={onlineSettingsOpen}
                onClose={() => setOnlineSettingsOpen(false)}
                gameType="RONDA"
                getGameSnapshot={() => ({
                    roomId,
                    connectionStatus,
                    scores,
                    matchScore,
                    dealerIndex: dealerIndexRef.current,
                    currentTurn: currentTurnRef.current,
                    mySeat: mySeatRef.current,
                })}
            />

            {connectionStatus === 'reconnecting' && (
                <Alert severity="warning" sx={{ position: 'fixed', top: 56, left: 0, right: 0, zIndex: 2000 }}>
                    {reconnectAttempt > 0
                        ? t('online.play.reconnecting_attempt', { attempt: reconnectAttempt })
                        : t('online.play.reconnecting')}
                </Alert>
            )}

            <Box sx={{
                width: needsRotation ? '100vh' : '100%',
                height: needsRotation ? 'calc(100vw - 56px)' : 'calc(100dvh - 56px)',
            }}>
                <GameTable onMount={handlePixiMount} backgroundColor={0x257241} />
            </Box>

            <RondaDecisionButtons
                visible={Boolean(!spectator && declarationPhase && declarationPhase.awaitingSeat === mySeatRef.current)}
                decisions={declarationPhase?.myDecisions ?? []}
                declaration={declarationPhase?.myDeclaration ?? null}
                onDecision={handleDeclarationDecision}
                disabled={processingDecision}
                playerName={playersBySeatRef.current?.[mySeatRef.current]?.name || t('online.play.title')}
            />

            <CaidaOpportunityButtons
                visible={isMyCaidaOpportunity}
                caidaType={caidaOpportunity?.potentialCaidaType === 'san_vicente' ? 'san_vicente' : 'contra'}
                cardValue={caidaOpportunity?.cardValue ?? null}
                onDecision={handleCaidaDecision}
                disabled={processingCaidaDecision}
                playerName={playersBySeatRef.current?.[mySeatRef.current]?.name || t('online.play.title')}
                timeRemaining={caidaTimeRemaining}
            />

            {!spectator && (
                <ChatPanel
                    variant="floating"
                    messages={chat.messages}
                    onSend={chat.sendMessage}
                    myUserId={user?.id}
                    disabled={chat.isSending}
                    open={chatToggle.expanded}
                    onClose={chatToggle.toggle}
                />
            )}

            <ShufflingModal
                open={Boolean(shufflingDealer)}
                playerName={shufflingDealer?.name}
                playerAvatar={shufflingDealer?.avatar}
            />

            <RondaHandSummaryModal
                open={Boolean(handSummaryData)}
                summaryData={handSummaryData}
                players={[0, 1, 2, 3].map(toHeaderPlayer)}
                onContinue={handleHandSummaryContinue}
            />

            <RoundTransitionModal
                open={Boolean(roundTransition)}
                winnerTeamId={roundTransition?.winningTeam}
                matchScore={roundTransition?.matchScore}
                nextGameName={
                    roundTransition?.nextGameType === 'BACIGA' ? t('online.lobby.game_type_baciga') : t('online.lobby.game_type_ronda')
                }
                playersBySeat={playersBySeatRef.current}
                hasAcked={hasAckedTransition}
                ackDeadlineAt={roundTransition?.ackDeadlineAt}
                onContinue={handleRoundTransitionContinue}
                readOnly={spectator}
            />

            <MatchWinnerModal
                open={Boolean(matchOver)}
                winnerTeamId={matchOver?.winningTeam}
                playersBySeat={playersBySeatRef.current}
                onBackToLobby={handleBackToLobby}
            />

            <Dialog open={Boolean(fatalError)}>
                <DialogTitle>{t('online.play.game_cancelled')}</DialogTitle>
                <DialogContent>
                    <Typography>{fatalError}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={handleBackToLobby}>
                        {t('online.lobby.title')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
